/**
 * HIOZ (hioz.studio) — video-first AI guest guide platform.
 *
 * HIOZ has its own dedicated subscription model, billed on the shared
 * ReHub Stripe customer:
 *
 *   HIOZ subscription = live property entitlement.
 *   ReHub credits     = optional premium AI/media processing.
 *
 * A "live property" is a HIOZ property whose guest guide is currently
 * published and available to guests. Only Live properties count towards
 * the subscription allowance — Draft, Paused and Archived properties are
 * unlimited and never billed. ReHub credits are NEVER used to keep a
 * property live, and guests are never charged (unlimited visits, views
 * and QR scans).
 *
 * This file is the single source of truth for HIOZ pricing/limits on the
 * client. The server mirror lives in
 * `supabase/functions/_shared/hioz/plans.ts` (imported by stripe-webhook
 * and hioz-billing) — keep the two in sync. Entitlements land on
 * `profiles.hioz_*` via the shared stripe-webhook and are enforced
 * server-side by the `get_hioz_entitlement` / `hioz_publish_guide` RPCs.
 *
 * Credit costs for AI work (guide creation, translation) are unchanged;
 * server source of truth: `supabase/functions/_shared/hioz/costs.ts`.
 */

import { detectBillingCurrency, formatMoney } from './currency';

export type HiozPlanId = 'starter' | 'host' | 'pro' | 'portfolio' | 'enterprise';
export type HiozBillingInterval = 'monthly' | 'annual';
export type HiozCurrency = 'GBP' | 'EUR' | 'USD';

/**
 * Global HIOZ billing configuration. Change prices/limits here (and in
 * the server mirror) — never hardcode them in components.
 * GBP is canonical; USD is the published US equivalent (not a live FX
 * conversion — clean marketing price points, like the credit packs).
 */
export const HIOZ_BILLING = {
  defaultCurrency: 'GBP' as HiozCurrency,
  /** Price per additional live property on any paid plan. */
  additionalPropertyMonthlyGBP: 4,
  additionalPropertyMonthlyEUR: 5,
  additionalPropertyMonthlyUSD: 5,
  /** Annual price per additional live property (~2 months free). */
  additionalPropertyAnnualGBP: 40,
  additionalPropertyAnnualEUR: 50,
  additionalPropertyAnnualUSD: 50,
  /** Annual plan pricing = monthly × (12 − annualMonthsFree). */
  annualMonthsFree: 2,
  /**
   * Building a HIOZ is always free. The one-time live trial (no card
   * required) starts when the FIRST property is made live — never at
   * signup, property creation or video upload. One per account,
   * deleting properties does not reset it.
   */
  trialDays: 14,
  trialLiveProperties: 1,
  /** Grace period after a failed payment before live guides are paused. */
  gracePeriodDays: 7,
} as const;

/** Customer-facing trial/marketing copy — keep messaging in one place. */
export const HIOZ_TRIAL_COPY = {
  build: 'Build your HIOZ for free.',
  microcopy: 'Build for free. No credit card required.',
  trial: 'First property free for 14 days.',
  trialExpanded: 'Try your first live property free for 14 days.',
  valueLine: 'Fewer guest questions. Better arrivals. Happier guests.',
} as const;

/**
 * Founding Host launch offer — Starter only, monthly billing.
 *
 * £4.99/month for the first 6 PAID months, then automatically £9.99/month
 * (Stripe Subscription Schedule — no manual plan change). The deadline
 * controls WHO qualifies (paid Starter must begin before it), never when
 * a subscriber's discount ends: someone subscribing on 31 Dec 2026 still
 * gets 6 full discounted months. The 14-day free live trial is separate
 * and never counts towards the 6 paid months. Once per account —
 * cancelling, resubscribing or deleting properties never grants another
 * discounted period. Server mirror: supabase/functions/_shared/hioz/plans.ts.
 */
export const HIOZ_FOUNDING_OFFER = {
  plan: 'starter' as HiozPlanId,
  /** Discount applies to monthly billing only. */
  interval: 'monthly' as HiozBillingInterval,
  monthlyPriceGBP: 4.99,
  monthlyPriceEUR: 5.99,
  monthlyPriceUSD: 6.49,
  /** Number of discounted paid billing periods. */
  months: 6,
  /** Eligibility deadline (UTC) — subscription must START before this. */
  deadline: '2027-01-01T00:00:00Z',
} as const;

/** Is the Founding Host offer still open to new Starter subscribers? */
export function isHiozFoundingOfferOpen(now: Date = new Date()): boolean {
  return now.getTime() < new Date(HIOZ_FOUNDING_OFFER.deadline).getTime();
}

export function hiozFoundingPrice(currency: HiozCurrency = 'GBP'): number {
  if (currency === 'USD') return HIOZ_FOUNDING_OFFER.monthlyPriceUSD;
  if (currency === 'EUR') return HIOZ_FOUNDING_OFFER.monthlyPriceEUR;
  return HIOZ_FOUNDING_OFFER.monthlyPriceGBP;
}

/**
 * Founding Host copy — never imply the discount is permanent (no
 * "forever" / "locked-in" wording), and always show the renewal price.
 */
export const HIOZ_FOUNDING_COPY = {
  badge: 'Founding Host offer',
  deadline: 'Join before 1 January 2027',
  duration: 'for your first 6 months',
  trialNote: 'Build free. Your first property can be live for 14 days before billing starts.',
} as const;

export interface HiozPlan {
  id: HiozPlanId;
  name: string;
  /** Live properties included in the base price. */
  includedLiveProperties: number;
  /** Monthly display price. null = "talk to us" (Enterprise). */
  monthlyPriceGBP: number | null;
  monthlyPriceEUR: number | null;
  monthlyPriceUSD: number | null;
  /** Annual display price (configurable; ~2 months free). null = custom. */
  annualPriceGBP: number | null;
  annualPriceEUR: number | null;
  annualPriceUSD: number | null;
  blurb: string;
  highlights: string[];
  popular?: boolean;
}

const annual = (monthly: number) =>
  Math.round(monthly * (12 - HIOZ_BILLING.annualMonthsFree) * 100) / 100;

export const HIOZ_PLANS: HiozPlan[] = [
  {
    id: 'starter',
    name: 'Founding Host',
    includedLiveProperties: 1,
    monthlyPriceGBP: 9.99,
    monthlyPriceEUR: 11.99,
    monthlyPriceUSD: 12.99,
    annualPriceGBP: annual(9.99),
    annualPriceEUR: annual(11.99),
    annualPriceUSD: annual(12.99),
    blurb: 'Your first live property, fully guided.',
    highlights: [
      '1 live property',
      'AI video guest guide',
      'Ask HIOZ',
      'Multilingual guest experience',
      'QR/link sharing',
      'Unlimited draft properties',
      'Unlimited paused properties',
      'Unlimited archived properties',
    ],
  },
  {
    id: 'host',
    name: 'Host Plus',
    includedLiveProperties: 3,
    monthlyPriceGBP: 19,
    monthlyPriceEUR: 23,
    monthlyPriceUSD: 25,
    annualPriceGBP: annual(19),
    annualPriceEUR: annual(23),
    annualPriceUSD: annual(25),
    blurb: 'Hosts with a handful of properties.',
    highlights: [
      'Up to 3 live properties',
      'Everything in Founding Host',
      'Guest analytics',
      'All 9 guide languages',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    includedLiveProperties: 10,
    monthlyPriceGBP: 49,
    monthlyPriceEUR: 59,
    monthlyPriceUSD: 59,
    annualPriceGBP: annual(49),
    annualPriceEUR: annual(59),
    annualPriceUSD: annual(59),
    popular: true,
    blurb: 'Growing portfolios and co-hosts.',
    highlights: [
      'Up to 10 live properties',
      'Everything in Host Plus',
      'Priority processing',
    ],
  },
  {
    id: 'portfolio',
    name: 'Portfolio',
    includedLiveProperties: 20,
    monthlyPriceGBP: 79,
    monthlyPriceEUR: 95,
    monthlyPriceUSD: 99,
    annualPriceGBP: annual(79),
    annualPriceEUR: annual(95),
    annualPriceUSD: annual(99),
    blurb: 'Property managers and agencies.',
    highlights: [
      'Up to 20 live properties',
      'Everything in Pro',
      'Priority support',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    includedLiveProperties: 1_000_000,
    monthlyPriceGBP: null,
    monthlyPriceEUR: null,
    monthlyPriceUSD: null,
    annualPriceGBP: null,
    annualPriceEUR: null,
    annualPriceUSD: null,
    blurb: 'Large property managers — custom limits and onboarding.',
    highlights: [
      'Custom live property allowance',
      'Custom onboarding',
      'Dedicated support',
    ],
  },
];

export function getHiozPlan(plan: string | null | undefined): HiozPlan {
  return HIOZ_PLANS.find((p) => p.id === plan) ?? HIOZ_PLANS[0];
}

/** Paid, self-serve plans (excludes Enterprise). */
export const HIOZ_SELF_SERVE_PLANS = HIOZ_PLANS.filter((p) => p.monthlyPriceGBP !== null);

export function hiozPlanPrice(
  plan: HiozPlan,
  interval: HiozBillingInterval,
  currency: HiozCurrency = 'GBP',
): number | null {
  if (currency === 'USD') {
    return interval === 'annual' ? plan.annualPriceUSD : plan.monthlyPriceUSD;
  }
  if (currency === 'EUR') {
    return interval === 'annual' ? plan.annualPriceEUR : plan.monthlyPriceEUR;
  }
  return interval === 'annual' ? plan.annualPriceGBP : plan.monthlyPriceGBP;
}

export function hiozAdditionalPropertyPrice(
  interval: HiozBillingInterval,
  currency: HiozCurrency = 'GBP',
): number {
  if (currency === 'USD') {
    return interval === 'annual'
      ? HIOZ_BILLING.additionalPropertyAnnualUSD
      : HIOZ_BILLING.additionalPropertyMonthlyUSD;
  }
  if (currency === 'EUR') {
    return interval === 'annual'
      ? HIOZ_BILLING.additionalPropertyAnnualEUR
      : HIOZ_BILLING.additionalPropertyMonthlyEUR;
  }
  return interval === 'annual'
    ? HIOZ_BILLING.additionalPropertyAnnualGBP
    : HIOZ_BILLING.additionalPropertyMonthlyGBP;
}

/** Total recurring cost for a plan + additional live properties. null = custom. */
export function hiozTotalPrice(
  planId: string | null | undefined,
  additionalProperties: number,
  interval: HiozBillingInterval = 'monthly',
  currency: HiozCurrency = 'GBP',
): number | null {
  const plan = getHiozPlan(planId);
  const base = hiozPlanPrice(plan, interval, currency);
  if (base === null) return null;
  return base + Math.max(0, additionalProperties) * hiozAdditionalPropertyPrice(interval, currency);
}

export interface HiozPlanRecommendation {
  plan: HiozPlan;
  currentMonthlyCost: number;
  recommendedMonthlyCost: number;
  savingPerMonth: number;
}

/**
 * If the user's plan + additional properties cost at least as much as a
 * bigger plan that covers the same capacity, recommend it. Never
 * auto-upgrades — display only.
 */
export function recommendHiozPlan(
  planId: string | null | undefined,
  additionalProperties: number,
  currency: HiozCurrency = 'GBP',
): HiozPlanRecommendation | null {
  const current = getHiozPlan(planId);
  const currentCost = hiozTotalPrice(current.id, additionalProperties, 'monthly', currency);
  if (currentCost === null || additionalProperties <= 0) return null;

  const capacityNeeded = current.includedLiveProperties + additionalProperties;
  let best: HiozPlanRecommendation | null = null;
  for (const candidate of HIOZ_SELF_SERVE_PLANS) {
    if (candidate.id === current.id) continue;
    if (candidate.includedLiveProperties < capacityNeeded) continue;
    const cost = hiozPlanPrice(candidate, 'monthly', currency);
    if (cost !== null && cost <= currentCost && (!best || cost < best.recommendedMonthlyCost)) {
      best = {
        plan: candidate,
        currentMonthlyCost: currentCost,
        recommendedMonthlyCost: cost,
        savingPerMonth: currentCost - cost,
      };
    }
  }
  return best;
}

export function formatHiozPrice(amount: number | null, currency: HiozCurrency = 'GBP'): string {
  if (amount === null) return 'Custom';
  return formatMoney(amount, currency);
}

/**
 * Display currency: subscription currency always wins. Otherwise detect
 * from locale/country (UK → GBP, eurozone → EUR, US → USD).
 */
export function detectHiozCurrency(): HiozCurrency {
  return detectBillingCurrency();
}

// =============================================================================
// Credit costs (display only — server mirrors in _shared/hioz/costs.ts).
// Credits pay for AI work only, never for keeping a property live.
// =============================================================================

export const HIOZ_CREDIT_COSTS = {
  /** AI guide creation: per started minute of walkthrough video (min 1/video). */
  guidePerMinute: 1,
  /** Text translation of a whole guide into one language. */
  translationPerLanguage: 1,
  /** Future AI voiceover per minute per language (architected, not in MVP). */
  voiceoverPerMinutePerLanguage: 2,
} as const;

/** Credits to build the AI guide for a walkthrough of the given duration. */
export function hiozGuideCreditsForDuration(durationMs: number): number {
  return Math.max(1, Math.ceil(durationMs / 60_000) * HIOZ_CREDIT_COSTS.guidePerMinute);
}
